import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Button, Drawer, Portal} from '@chakra-ui/react3'
import {CancelIcon} from '../icons'
import useAnimationFrameCallback from '../../hooks/useAnimationFrameCallback'
import {Chakra3Theme} from '../../theme'

/**
 * Drawer shell used by the overlay manager via createOverlayType.
 * Handles scroll-shadow state, close button, and initial focus management.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.content - Content rendered inside the drawer.
 * @param {boolean} [props.open=false] - Controlled open state (from overlay manager).
 * @param {Function} [props.onOpenChange] - Called when open state should change.
 * @param {Function} [props.onExitComplete] - Called after exit animation completes.
 * @returns {JSX.Element}
 */
const LeapDrawer = ({content, open = false, theme, onOpenChange, onExitComplete}) => {
    const {formatMessage} = useIntl()
    const [isScrolled, setIsScrolled] = useState(false)
    const closeBtnRef = useRef(null)
    const onContentScroll = useAnimationFrameCallback((event) => {
        setIsScrolled((event.currentTarget || event.target).scrollTop > 0)
    })

    return (
        <Drawer.Root
            initialFocusEl={() => closeBtnRef.current}
            open={open}
            size="md"
            variant="configurator"
            placement={{base: 'bottom', xs: 'end'}}
            onOpenChange={onOpenChange}
            onExitComplete={onExitComplete}
        >
            <Portal>
                <Chakra3Theme theme={theme}>
                    <Drawer.Backdrop />
                    <Drawer.Positioner>
                        <Drawer.Content
                            onScroll={onContentScroll}
                            data-drawer-scrolled={isScrolled ? 'true' : 'false'}
                        >
                            <Drawer.CloseTrigger asChild>
                                <Button
                                    ref={closeBtnRef}
                                    variant="mediaFilled"
                                    size="md"
                                    aria-label={formatMessage({
                                        defaultMessage: 'Close',
                                        id: 'global.close'
                                    })}
                                >
                                    <CancelIcon />
                                </Button>
                            </Drawer.CloseTrigger>

                            {content}
                        </Drawer.Content>
                    </Drawer.Positioner>
                </Chakra3Theme>
            </Portal>
        </Drawer.Root>
    )
}

LeapDrawer.propTypes = {
    content: PropTypes.node,
    open: PropTypes.bool,
    theme: Chakra3Theme.propTypes.theme,
    onOpenChange: PropTypes.func,
    onExitComplete: PropTypes.func
}

export default LeapDrawer
